import "./MainCanvas.css";
import { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLearnCtx } from "./LearnPage";
import FrameWrapper from "./FrameWrapper";
import supportImg from "../../images/Support.png";
import { useDispatch, useSelector } from "react-redux";
import { calcPercentExercisesComplete } from "../Global/ProblemUtils";
import { flattenFrames } from "../../utils/learnUtils";
import {updateUserCoursesTaken} from "../../actions/user.js"

/** Format seconds as natural language: "2 hours, 25 minutes and 29 seconds" */
function formatTimeNatural(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const parts = [];
  if (h > 0) parts.push(`${h} hour${h !== 1 ? "s" : ""}`);
  if (m > 0) parts.push(`${m} minute${m !== 1 ? "s" : ""}`);
  parts.push(`${s} second${s !== 1 ? "s" : ""}`);
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts[0]}, ${parts[1]} and ${parts[2]}`;
}

/**
 * Full-screen completion lightbox.
 */
function FinishedModal({ onClose }) {

  // Get the course id from the URL
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch() ; 

  const problemsTriedAndAnswers = useSelector((state) => state.ProgressReducer.problemsTried);
  const problemIds = useSelector((state) => state.ContentReducer.problemIds);
  const answeredPct = calcPercentExercisesComplete(problemIds,problemsTriedAndAnswers);

  // Retrieve time and format into hours / minutes / seconds
  const previousElapsedTime = useSelector(
    (state) => state.ProgressReducer.elapsedTimeCopy,
  );
  const formatTime = (timer) => {
    const hours = Math.floor(timer / 3600000).toString().padStart(2, "0");
    const minutes = Math.floor((timer / 60000) % 60).toString().padStart(2, "0");
    const seconds = Math.floor((timer / 1000) % 60).toString().padStart(2, "0");
    return { hours, minutes, seconds };
  };
  const { hours, minutes, seconds } = formatTime(previousElapsedTime);
  const donationAmount = Math.max(1, hours) * 5;

  return (
    <div
      className="finished-modal__backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Module complete"
    >
      <div
        className="finished-modal__card"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="finished-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>

        <h2 className="finished-modal__heading">Great work!</h2>
        <p className="finished-modal__subheading">
          You have finished this module.
        </p>

        <p className="finished-modal__stat">
          You completed <strong>{answeredPct.toFixed(0)}%</strong> of the exercises.
        </p>
        <p className="finished-modal__stat">
          You took <strong>{hours} hours and {minutes} minutes</strong>.
        </p>

        <p className="finished-modal__body">
          I would like to keep this website free to use, but it costs time (a
          lot) and money (some) to run. Consider
          donating! I suggest a contribution of $5 per hour of study time (rounded up)
          which comes to <strong>${donationAmount}</strong> for this unit, but
          if you can be more generous, that would be great.
        </p>

        <a
          href="https://ko-fi.com/simonprince"
          target="_blank"
          rel="noopener noreferrer"
          className="finished-modal__kofi-link"
        >
          <img
            src={supportImg}
            alt="Support me on Ko-fi"
            className="finished-modal__kofi-img"
          />
        </a>

        <p className="finished-modal__footer-text">
          After donating (or not) click the button below to mark this unit as
          finished and return to the home page.
        </p>

        <button
          className="finished-modal__complete-btn"
          onClick={() => {
              // Store locally that we have compleed
            var user = JSON.parse(localStorage.getItem('profile'))
            const courseIndex = user?.result?.coursesTaken.findIndex((item)=>{return (item.courseID===id)})
            user.result.coursesTaken[courseIndex].completed= true
            localStorage.setItem('profile', JSON.stringify(user))
            // Dispatch to server
            const courseTaken = {courseID:id, completed:true, dateLastSeen:Date.now(),  elapsedTime:previousElapsedTime, percentComplete:user.result.coursesTaken[courseIndex].percentComplete, percentCompleteExercises:answeredPct }
            dispatch(updateUserCoursesTaken(id, courseTaken))
           
            navigate("/")
          }}
        >
          MARK UNIT AS COMPLETE
        </button>
      </div>
    </div>
  );
}

/**
 * Center panel: renders all frames in order.
 */
export default function MainCanvas() {

  const rawChapters = useSelector((state) => state.ContentReducer.content);
/* ─── Derived data (stable across renders) ──────────────────── */
  const frames = useMemo(() => flattenFrames(rawChapters), [rawChapters]);
  const [showFinished, setShowFinished] = useState(false);

  if(frames===undefined || frames.length==0)
  {  
    return (
      <div className="canvas">
        <div className="frame__extension_left"></div>
          <section className="frame">
              {/* Content items */}
            <div className="frame__content">
              <div className="canvas__loading">
                  <span className="canvas__loading-text">Loading</span>
                  <span className="canvas__loading-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
              </span>
            </div>
            </div>
          </section>
          {/* Icons — grid column 2, outside the white paper */}
          <div className="frame__icons--hidden" aria-label="Frame tools"/>
          <div className="frame__extension_right"></div>
        </div>  
    )}
    
    
  return (
    <div className="canvas">      
      {frames.map(({ frame, chapterTitle, sectionId, sectionIndex }, i) => (
        <FrameWrapper
          key={frame._id ?? i}
          frame={frame}
          chapterTitle={chapterTitle}
          sectionId = {sectionId}
          sectionIndex={sectionIndex}
          isFirst={i === 0}
          isLast={i === frames.length - 1}
          onFinished={() => setShowFinished(true)}
        />
      ))}
      

      {/* Bottom padding so last frame can be scrolled into view */}
      <div style={{ height: "40vh", gridColumn: "1" }} aria-hidden="true" />

      {showFinished && (
        <FinishedModal
          onClose={() => setShowFinished(false)}
        />
      )}
    </div>
  );
}
